import { useEffect, useMemo, useState } from "react";

import { fetchRentedPropertyListingPhotos } from "../services/fetchRentedPropertyListingPhotos";
import { IRentedPropertyDetail, IRentedPropertyPhoto } from "../types/ownerDashboard.types";

type RentedPropertyPhotoSource = Pick<
  IRentedPropertyDetail,
  "property_id" | "listing_property_id" | "photos"
>;

function resolveListingPhotoFetchId(
  detail: RentedPropertyPhotoSource | null
): number | null {
  if (!detail) {
    return null;
  }

  const listingPropertyId = Number(detail.listing_property_id);
  if (Number.isFinite(listingPropertyId) && listingPropertyId > 0) {
    return listingPropertyId;
  }

  const propertyId = Number(detail.property_id);
  if (Number.isFinite(propertyId) && propertyId > 0) {
    return propertyId;
  }

  return null;
}

export function useRentedPropertyPhotos(detail: RentedPropertyPhotoSource | null) {
  const embeddedPhotos = detail?.photos ?? [];
  const embeddedPhotoKey = useMemo(
    () => embeddedPhotos.map((photo) => photo.id).join(","),
    [embeddedPhotos]
  );
  const [photos, setPhotos] = useState<IRentedPropertyPhoto[]>(embeddedPhotos);
  const [isLoadingPhotos, setIsLoadingPhotos] = useState(false);

  useEffect(() => {
    if (embeddedPhotos.length > 0) {
      setPhotos(embeddedPhotos);
      setIsLoadingPhotos(false);
      return;
    }

    const fetchId = resolveListingPhotoFetchId(detail);
    if (fetchId == null) {
      setPhotos([]);
      setIsLoadingPhotos(false);
      return;
    }

    let cancelled = false;
    setIsLoadingPhotos(true);

    fetchRentedPropertyListingPhotos(fetchId)
      .then((fetchedPhotos) => {
        if (!cancelled) {
          setPhotos(fetchedPhotos);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setPhotos([]);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoadingPhotos(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [detail, embeddedPhotoKey, embeddedPhotos]);

  return { photos, isLoadingPhotos };
}
